import { type ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { DateField, Show, useGetList, useRecordContext } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { AffiliateNetworkMarket } from '../dataProvider';
import { formatRate } from '../stores/formatRate';

/**
 * Affiliate network detail (Show) — READ-ONLY (D-networks-readonly). The
 * network's reference details plus its per-market commission defaults (from
 * `affiliateNetworkMarkets`). No Edit affordance exists — there is no write
 * capability for networks at all.
 */

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

// Per-market commission defaults — genuine reference data on a separate entity.
const CommissionDefaults = ({ networkId }: { networkId: number }) => {
  const { data } = useGetList<AffiliateNetworkMarket>(
    'affiliateNetworkMarkets',
    {
      pagination: { page: 1, perPage: 50 },
      sort: { field: 'market', order: 'ASC' },
      filter: { networkId },
    },
  );
  if (!data || data.length === 0) {
    return <Field label="Commission defaults">—</Field>;
  }
  return (
    <Field label="Commission defaults (per market)">
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
        {data.map((m) => (
          <span key={m.id}>
            {m.market.toUpperCase()}: {formatRate(m.defaultCommissionRate)}
          </span>
        ))}
      </Stack>
    </Field>
  );
};

const NetworkShowLayout = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
            {record.name as string}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            <Field label="Slug">{record.slug as string}</Field>
            <Field label="Tracking domain">
              {record.trackingDomain as string}
            </Field>
            <Field label="Click param">{record.clickParam as string}</Field>
            <Field label="Deeplink param">
              {record.deeplinkParam as string}
            </Field>
            <Field label="Stores on this network">
              {String(record.storeCount ?? 0)}
            </Field>
            <Field label="Last imported">
              <DateField source="lastImportedAt" />
            </Field>
          </Box>
          <Divider sx={{ my: 2 }} />
          <CommissionDefaults networkId={record.id as number} />
        </CardContent>
      </Card>
    </Box>
  );
};

/** The detail view. Exported for testing without the access wrapper. */
export const NetworkShowView = () => (
  <Show title="Network" actions={false} resource="affiliateNetworks">
    <NetworkShowLayout />
  </Show>
);

/** Gated entry point registered as the `affiliateNetworks` Resource's show. */
export const NetworkShow = () => (
  <RequireAccess capability="networks:read">
    <NetworkShowView />
  </RequireAccess>
);
